import { PAYMENT_METHODS } from '../constants';
import { countryRegions } from '../constants/country';

const DEFAULT_TOKENIZATION_SOURCE = 'billing-widget';

const MERCHANT_WALLET_TYPE_TO_PAYMENT_METHOD = {
	card_token: PAYMENT_METHODS.creditCard,
	direct_debit: PAYMENT_METHODS.directDebit,
	su_payouts: PAYMENT_METHODS.suPayout,
	su_payment_methods: PAYMENT_METHODS.suPaymentMethods,
	su_business_account: PAYMENT_METHODS.suBusinessAccount,
};

export class MerchantWalletMapper {
	/**
	 * Maps billing details to the format expected by the Merchant Wallet API.
	 * @param {Object} params
	 * @param {string} [params.email]
	 * @param {string} [params.phoneNumber]
	 * @param {Object} params.billingAddress
	 * @returns {Object}
	 */
	static mapUpsertCustomerRequest({ email, phoneNumber, billingAddress }) {
		const address = billingAddress || {};
		const resolvedEmail = email || address.email;
		const resolvedPhone = phoneNumber || address.phone || address.phoneNumber;

		const billing_address = {
			first_name: address.firstName,
			last_name: address.lastName,
			company_name: address.company || address.companyName,
			email: resolvedEmail,
			address_line_1: address.addressLine1,
			city: address.city,
			country_code: address.country || address.countryCode,
			post_code: address.zip || address.postCode,
		};

		if (resolvedPhone) {
			billing_address.phone_number = resolvedPhone;
		}
		if (address.addressLine2) {
			billing_address.address_line_2 = address.addressLine2;
		}
		if (address.regionName || address.state) {
			billing_address.region_name = address.regionName || address.state;
		}
		if (address.vatNumber) {
			billing_address.tax_numbers = [{
				tax_name: 'VAT',
				tax_number: address.vatNumber,
			}];
		}

		const request = { billing_address };

		if (resolvedEmail) {
			request.email = resolvedEmail;
		}
		if (resolvedPhone) {
			request.phone_number = resolvedPhone;
		}

		return request;
	}

	/**
	 * Maps billing details to PatchBillingAddressRequest for Merchant Wallet.
	 * @param {Object} params
	 * @param {Object} [params.billingAddress]
	 * @param {string} [params.vatNumber]
	 * @returns {Object}
	 */
	static mapPatchBillingAddressRequest({ billingAddress, vatNumber } = {}) {
		if (vatNumber !== undefined && !billingAddress) {
			if (!vatNumber || !String(vatNumber).trim()) {
				return { tax_numbers: [] };
			}

			return {
				tax_numbers: [{
					tax_name: 'VAT',
					tax_number: vatNumber,
				}],
			};
		}

		const address = billingAddress || {};
		const request = {};

		if (Object.prototype.hasOwnProperty.call(address, 'firstName')) {
			request.first_name = address.firstName;
		}
		if (Object.prototype.hasOwnProperty.call(address, 'lastName')) {
			request.last_name = address.lastName;
		}
		if (Object.prototype.hasOwnProperty.call(address, 'company')
			|| Object.prototype.hasOwnProperty.call(address, 'companyName')) {
			request.company_name = address.company || address.companyName;
		}
		if (Object.prototype.hasOwnProperty.call(address, 'email')) {
			request.email = address.email;
		}
		if (Object.prototype.hasOwnProperty.call(address, 'phone')) {
			request.phone_number = address.phone;
		}
		if (Object.prototype.hasOwnProperty.call(address, 'addressLine1')) {
			request.address_line_1 = address.addressLine1;
		}
		if (Object.prototype.hasOwnProperty.call(address, 'addressLine2')) {
			request.address_line_2 = address.addressLine2;
		}
		if (Object.prototype.hasOwnProperty.call(address, 'city')) {
			request.city = address.city;
		}
		if (Object.prototype.hasOwnProperty.call(address, 'country')
			|| Object.prototype.hasOwnProperty.call(address, 'countryCode')) {
			request.country_code = address.country || address.countryCode;
		}
		if (Object.prototype.hasOwnProperty.call(address, 'zip')
			|| Object.prototype.hasOwnProperty.call(address, 'postCode')) {
			request.post_code = address.zip || address.postCode;
		}
		if (Object.prototype.hasOwnProperty.call(address, 'state')
			|| Object.prototype.hasOwnProperty.call(address, 'regionName')) {
			request.region_name = address.regionName || address.state;
		}

		return request;
	}

	/**
	 * Maps the Merchant Wallet customer response to the format expected by the application.
	 * @param {Object} customer
	 * @returns {Object|null}
	 */
	static mapCustomerResponse(customer) {
		if (!customer) {
			return null;
		}

		const billingAddress = (customer.billing_addresses && customer.billing_addresses[0]) || null;
		const paymentMethods = (customer.payment_methods || [])
			.map((paymentMethod) => this.mapPaymentMethodResponse(paymentMethod))
			.filter(Boolean);
		const defaultPaymentMethod = paymentMethods.find((paymentMethod) => paymentMethod.isDefault)
			|| paymentMethods[0]
			|| null;

		return {
			id: customer.id,
			reference: customer.reference,
			merchantCode: customer.merchant_code,
			legacyId: customer.legacy_id,
			billingDetails: billingAddress ? this.mapBillingAddressResponse(billingAddress) : null,
			paymentMethods,
			paymentMethod: defaultPaymentMethod,
			hasPaymentMethod: paymentMethods.length > 0,
			createdAt: customer.created_at,
			updatedAt: customer.updated_at,
		};
	}

	/**
	 * @param {Object} billingAddress
	 * @returns {Object}
	 */
	static mapBillingAddressResponse(billingAddress) {
		const vatNumber = billingAddress.tax_numbers?.find((tax) => tax.tax_name === 'VAT')?.tax_number;
		const country = billingAddress.country_code;
		const regionName = billingAddress.region_name;
		const countryHasRegions = Object.prototype.hasOwnProperty.call(countryRegions, country);
		const matchedRegion = (countryRegions[country] || [])
			.find((region) => region.regionName === regionName);
		const resolvedRegionName = matchedRegion?.regionName ?? regionName ?? null;

		return {
			firstName: billingAddress.first_name,
			lastName: billingAddress.last_name,
			company: billingAddress.company_name,
			email: billingAddress.email,
			phone: billingAddress.phone_number,
			addressLine1: billingAddress.address_line_1,
			addressLine2: billingAddress.address_line_2,
			city: billingAddress.city,
			country,
			zip: billingAddress.post_code,
			state: countryHasRegions ? '' : regionName,
			regionId: matchedRegion?.regionId ?? null,
			regionName: resolvedRegionName,
			vatNumber,
		};
	}

	/**
	 * @param {Object} params
	 * @param {string} [params.source]
	 * @param {string} params.redirectUrl
	 * @param {string} [params.returnUrl]
	 * @returns {Object}
	 */
	static mapCreateTokenizationRequest({ source, redirectUrl, returnUrl }) {
		const request = {
			source: source || DEFAULT_TOKENIZATION_SOURCE,
			redirect_url: redirectUrl,
		};

		if (returnUrl) {
			request.return_url = returnUrl;
		}

		return request;
	}

	/**
	 * @param {Object} tokenization
	 * @returns {Object|null}
	 */
	static mapTokenizationResponse(tokenization) {
		if (!tokenization) {
			return null;
		}

		return {
			id: tokenization.id,
			customerId: tokenization.customer_id,
			paymentMethodId: tokenization.payment_method_id,
			status: tokenization.status,
			amount: tokenization.amount,
			metadata: tokenization.metadata ? {
				checkoutId: tokenization.metadata.checkout_id,
			} : undefined,
			checkoutId: tokenization.metadata?.checkout_id,
			source: tokenization.source,
			redirectUrl: tokenization.redirect_url,
			returnUrl: tokenization.return_url,
			createdAt: tokenization.created_at,
			updatedAt: tokenization.updated_at,
		};
	}

	/**
	 * @param {Object} [paymentMethod]
	 * @returns {Object|undefined}
	 */
	static mapConfirmTokenizationRequest(paymentMethod) {
		if (!paymentMethod) {
			return undefined;
		}

		const request = {
			payment_method: {
				set_as_default: paymentMethod.setAsDefault ?? false,
			},
		};

		if (paymentMethod.type === PAYMENT_METHODS.creditCard || paymentMethod.type === 'card_token') {
			request.payment_method.type = 'card_token';
			request.payment_method.card_token = {
				expiry: paymentMethod.expiry,
				cardholder_name: paymentMethod.cardholderName,
			};
		} else if (paymentMethod.type === PAYMENT_METHODS.directDebit || paymentMethod.type === 'direct_debit') {
			request.payment_method.type = 'direct_debit';
			request.payment_method.direct_debit = {
				iban: paymentMethod.iban || paymentMethod.directDebit?.iban,
			};
		} else if (paymentMethod.type) {
			request.payment_method.type = paymentMethod.type;
		}

		return request;
	}

	/**
	 * @param {Object} response - Confirm tokenization API data payload
	 * @returns {Object}
	 */
	static mapConfirmTokenizationResponse(response) {
		if (!response) {
			return null;
		}

		return {
			tokenization: this.mapTokenizationResponse(response.tokenization),
			customer: this.mapCustomerResponse(response.customer),
		};
	}

	/**
	 * @param {Object} response
	 * @returns {Object|null}
	 */
	static mapAvailablePaymentMethodsResponse(response) {
		if (!response) {
			return null;
		}

		const availableTypes = (response.available || []).map((item) => item.type);
		const active = (response.active || [])
			.map((paymentMethod) => this.mapPaymentMethodResponse(paymentMethod))
			.filter(Boolean)
			.filter((paymentMethod) => paymentMethod.status !== 'inactive');

		return {
			availablePaymentMethods: this.mapAvailableTypesToPaymentMethods(availableTypes),
			active,
		};
	}

	/**
	 * @param {string[]} availableTypes
	 * @returns {Object}
	 */
	static mapAvailableTypesToPaymentMethods(availableTypes) {
		const types = new Set(availableTypes);

		return {
			creditCard: types.has('card_token'),
			directDebit: types.has('direct_debit'),
			businessAccount: types.has('direct_debit'),
			suPayout: types.has('su_payouts'),
			suPaymentMethods: types.has('su_payment_methods'),
			suBusinessAccount: types.has('su_business_account'),
		};
	}

	/**
	 * Last-resort fallback when MW availability could not be loaded.
	 * Keeps embedder flags for tokenized methods only; hides payout, SU PM, and SUBA.
	 * @param {Object} embedderAvailablePaymentMethods
	 * @returns {Object}
	 */
	static mapEmbedderPaymentMethodsToTokenizedOnlyAvailability(embedderAvailablePaymentMethods) {
		return {
			creditCard: Boolean(embedderAvailablePaymentMethods.creditCard),
			directDebit: Boolean(embedderAvailablePaymentMethods.directDebit),
			businessAccount: Boolean(embedderAvailablePaymentMethods.businessAccount),
			suPayout: false,
			suPaymentMethods: false,
			suBusinessAccount: false,
		};
	}

	static mergeAvailablePaymentMethods(
		embedderAvailablePaymentMethods,
		merchantWalletAvailablePaymentMethods,
		activePaymentMethods = [],
	) {
		const activePaymentMethodTypes = new Set(
			activePaymentMethods.map((paymentMethod) => paymentMethod.type),
		);

		return {
			creditCard: embedderAvailablePaymentMethods.creditCard
				&& merchantWalletAvailablePaymentMethods.creditCard,
			directDebit: embedderAvailablePaymentMethods.directDebit
				&& merchantWalletAvailablePaymentMethods.directDebit,
			businessAccount: embedderAvailablePaymentMethods.businessAccount
				&& merchantWalletAvailablePaymentMethods.businessAccount,
			suPayout: embedderAvailablePaymentMethods.suPayout
				&& (
					merchantWalletAvailablePaymentMethods.suPayout
					|| activePaymentMethodTypes.has(PAYMENT_METHODS.suPayout)
				),
			suPaymentMethods: embedderAvailablePaymentMethods.suPaymentMethods
				&& (
					merchantWalletAvailablePaymentMethods.suPaymentMethods
					|| activePaymentMethodTypes.has(PAYMENT_METHODS.suPaymentMethods)
				),
			suBusinessAccount: embedderAvailablePaymentMethods.suBusinessAccount
				&& (
					merchantWalletAvailablePaymentMethods.suBusinessAccount
					|| activePaymentMethodTypes.has(PAYMENT_METHODS.suBusinessAccount)
				),
		};
	}

	/**
	 * @param {Object} paymentMethod
	 * @returns {Object|null}
	 */
	static mapPaymentMethodResponse(paymentMethod) {
		if (!paymentMethod) {
			return null;
		}

		const base = {
			id: paymentMethod.id,
			status: paymentMethod.status,
			isDefault: paymentMethod.default,
			createdAt: paymentMethod.created_at,
			updatedAt: paymentMethod.updated_at,
		};

		if (paymentMethod.type === 'card_token') {
			return {
				...base,
				type: PAYMENT_METHODS.creditCard,
				creditCard: {
					type: paymentMethod.card_token?.card_type || paymentMethod.card_token?.type,
					last4: paymentMethod.card_token?.last_4_digits || paymentMethod.card_token?.last_digits,
					expiry: paymentMethod.card_token?.expiry,
					cardholderName: paymentMethod.card_token?.cardholder_name,
					token: paymentMethod.card_token?.token,
				},
			};
		}

		if (paymentMethod.type === 'direct_debit') {
			return {
				...base,
				type: PAYMENT_METHODS.directDebit,
				directDebit: {
					maskedIban: paymentMethod.direct_debit?.masked_iban,
					accountHolder: paymentMethod.direct_debit?.account_holder_name
						|| paymentMethod.direct_debit?.accountholder_name,
					iban: paymentMethod.direct_debit?.iban,
					mandateReference: paymentMethod.direct_debit?.mandate_reference,
				},
			};
		}

		if (paymentMethod.type === 'su_payouts') {
			return {
				...base,
				type: PAYMENT_METHODS.suPayout,
				suPayout: {
					merchantCode: paymentMethod.su_payouts?.merchant_code,
				},
			};
		}

		if (paymentMethod.type === 'su_payment_methods') {
			return {
				...base,
				type: PAYMENT_METHODS.suPaymentMethods,
				suPaymentMethods: {
					merchantCode: paymentMethod.su_payment_methods?.merchant_code,
				},
			};
		}

		if (paymentMethod.type === 'su_business_account') {
			return {
				...base,
				type: PAYMENT_METHODS.suBusinessAccount,
				suBusinessAccount: {
					merchantCode: paymentMethod.su_business_account?.merchant_code,
				},
			};
		}

		const mappedType = MERCHANT_WALLET_TYPE_TO_PAYMENT_METHOD[paymentMethod.type] || paymentMethod.type;

		return {
			...base,
			type: mappedType,
		};
	}
}
